import { useEffect, useRef, useCallback } from "react";

/**
 * useIdleLogout
 *
 * Monitors user activity (mouse, keyboard, scroll, touch).
 * After `idleMinutes` of no activity:
 *   1. Shows a warning modal with a 60-second countdown.
 *   2. If the user doesn't interact, calls `onLogout`.
 *   3. Any activity during the warning dismisses it and resets the timer.
 *
 * @param {Function} onLogout     - Called when the idle timeout is confirmed.
 * @param {number}   idleMinutes  - Minutes of inactivity before warning (default: 30).
 * @param {number}   warnSeconds  - Seconds of warning countdown before logout (default: 60).
 * @param {boolean}  enabled      - Enable tracking only when authenticated.
 * @param {Function} onContinue   - Called when user clicks "Stay Logged In".
 * @param {number}   idleExpiryMs - Absolute backend idle-expiry timestamp in ms.
 */
const useIdleLogout = (
  onLogout,
  idleMinutes = 30,
  warnSeconds = 60,
  enabled = true,
  onContinue = null,
  idleExpiryMs = null,
) => {
  const idleTimer = useRef(null);
  const warnTimer = useRef(null);
  const modalRef = useRef(null);
  const countdownRef = useRef(null);
  const countdownValue = useRef(warnSeconds);
  const continueInFlightRef = useRef(false);

  const IDLE_MS = idleMinutes * 60 * 1000;

  // ── Remove warning modal ──────────────────────────────────
  const dismissModal = useCallback(() => {
    clearInterval(countdownRef.current);
    if (modalRef.current && document.body.contains(modalRef.current)) {
      document.body.removeChild(modalRef.current);
    }
    modalRef.current = null;
  }, []);

  // ── Perform logout ────────────────────────────────────────
  const performLogout = useCallback(() => {
    dismissModal();
    onLogout();
  }, [onLogout, dismissModal]);

  // ── Show warning modal ────────────────────────────────────
  const showWarning = useCallback(() => {
    if (modalRef.current) return; // already showing

    countdownValue.current = warnSeconds;

    // Backdrop
    const overlay = document.createElement("div");
    overlay.style.cssText = `
      position: fixed; inset: 0;
      background: rgba(0,0,0,0.55);
      z-index: 99998;
      display: flex; align-items: center; justify-content: center;
    `;

    // Modal card
    const card = document.createElement("div");
    card.style.cssText = `
      background: #fff;
      border-radius: 12px;
      padding: 36px 40px;
      max-width: 420px;
      width: 90%;
      text-align: center;
      box-shadow: 0 20px 60px rgba(0,0,0,0.25);
      font-family: inherit;
      z-index: 99999;
    `;

    const icon = document.createElement("div");
    icon.innerHTML = "⏱️";
    icon.style.cssText = "font-size: 48px; margin-bottom: 12px;";

    const title = document.createElement("h4");
    title.textContent = "Session About to Expire";
    title.style.cssText = "margin: 0 0 8px; font-size: 20px; font-weight: 700; color: #333;";

    const msg = document.createElement("p");
    msg.style.cssText = "color: #666; font-size: 14px; margin-bottom: 20px;";
    msg.textContent = `You'll be logged out in`;

    const countdown = document.createElement("div");
    countdown.id = "idle-countdown";
    countdown.style.cssText = `
      font-size: 42px; font-weight: 800;
      color: var(--primary, #886CC0);
      margin-bottom: 24px;
      line-height: 1;
    `;
    countdown.textContent = countdownValue.current;

    const subMsg = document.createElement("p");
    subMsg.style.cssText = "color: #666; font-size: 13px; margin-top: -16px; margin-bottom: 20px;";
    subMsg.textContent = "seconds due to inactivity.";

    const btn = document.createElement("button");
    btn.textContent = "Stay Logged In";
    btn.style.cssText = `
      background: var(--primary, #886CC0);
      color: #fff; border: none;
      padding: 12px 32px;
      border-radius: 8px;
      font-size: 15px; font-weight: 600;
      cursor: pointer;
      transition: opacity 0.2s;
    `;
    btn.onmouseover = () => (btn.style.opacity = "0.85");
    btn.onmouseout = () => (btn.style.opacity = "1");
    btn.onclick = async () => {
      clearTimeout(idleTimer.current);
      clearTimeout(warnTimer.current);
      if (typeof onContinue === "function") {
        const ok = await onContinue();
        if (!ok) return;
      }
      dismissModal();
      clearTimeout(idleTimer.current);
      clearTimeout(warnTimer.current);
      if (!idleExpiryMs) {
        resetTimer();
      }
    };

    card.appendChild(icon);
    card.appendChild(title);
    card.appendChild(msg);
    card.appendChild(countdown);
    card.appendChild(subMsg);
    card.appendChild(btn);
    overlay.appendChild(card);
    document.body.appendChild(overlay);
    modalRef.current = overlay;

    // Countdown tick
    countdownRef.current = setInterval(() => {
      countdownValue.current -= 1;
      const el = document.getElementById("idle-countdown");
      if (el) el.textContent = countdownValue.current;
      if (countdownValue.current <= 0) {
        clearInterval(countdownRef.current);
        performLogout();
      }
    }, 1000);
  }, [warnSeconds, performLogout, onContinue, dismissModal, idleExpiryMs]);

  const continueSessionFromActivity = useCallback(async () => {
    if (continueInFlightRef.current) return;
    if (typeof onContinue !== "function") return;

    continueInFlightRef.current = true;
    try {
      const ok = await onContinue();
      if (!ok) {
        performLogout();
        return;
      }
      dismissModal();
      clearTimeout(idleTimer.current);
      clearTimeout(warnTimer.current);
      clearInterval(countdownRef.current);
    } finally {
      continueInFlightRef.current = false;
    }
  }, [onContinue, performLogout, dismissModal]);

  // ── Reset idle timer ──────────────────────────────────────
  const resetTimer = useCallback(() => {
    dismissModal();
    clearTimeout(idleTimer.current);
    clearTimeout(warnTimer.current);

    idleTimer.current = setTimeout(() => {
      showWarning();
      warnTimer.current = setTimeout(performLogout, warnSeconds * 1000);
    }, IDLE_MS);
  }, [IDLE_MS, warnSeconds, showWarning, performLogout, dismissModal]);

  // ── Wire up activity listeners ────────────────────────────
  useEffect(() => {
    if (!enabled) return undefined;

    // Backend-synced absolute expiry mode:
    if (Number.isFinite(Number(idleExpiryMs)) && Number(idleExpiryMs) > 0) {
      const expiryMs = Number(idleExpiryMs);
      const now = Date.now();
      const msUntilExpiry = expiryMs - now;
      const msUntilWarning = expiryMs - warnSeconds * 1000 - now;

      clearTimeout(idleTimer.current);
      clearTimeout(warnTimer.current);
      clearInterval(countdownRef.current);
      dismissModal();

      if (msUntilExpiry <= 0) {
        performLogout();
        return undefined;
      }

      const showAbsoluteWarning = () => {
        showWarning();
        countdownValue.current = Math.max(
          0,
          Math.ceil((expiryMs - Date.now()) / 1000),
        );
        const el = document.getElementById("idle-countdown");
        if (el) el.textContent = countdownValue.current;

        clearInterval(countdownRef.current);
        countdownRef.current = setInterval(() => {
          const remaining = Math.max(
            0,
            Math.ceil((expiryMs - Date.now()) / 1000),
          );
          countdownValue.current = remaining;
          const countdownEl = document.getElementById("idle-countdown");
          if (countdownEl) countdownEl.textContent = remaining;
          if (remaining <= 0) {
            clearInterval(countdownRef.current);
            performLogout();
          }
        }, 1000);
      };

      const activityEvents = [
        "mousemove",
        "keydown",
        "mousedown",
        "touchstart",
        "scroll",
        "click",
      ];
      const onActivityDuringWarning = () => {
        if (modalRef.current) {
          continueSessionFromActivity();
        }
      };
      activityEvents.forEach((eventName) =>
        window.addEventListener(eventName, onActivityDuringWarning, {
          passive: true,
        }),
      );

      idleTimer.current = setTimeout(
        showAbsoluteWarning,
        Math.max(0, msUntilWarning),
      );
      warnTimer.current = setTimeout(performLogout, msUntilExpiry);

      return () => {
        activityEvents.forEach((eventName) =>
          window.removeEventListener(eventName, onActivityDuringWarning),
        );
        clearTimeout(idleTimer.current);
        clearTimeout(warnTimer.current);
        clearInterval(countdownRef.current);
        dismissModal();
      };
    }

    const events = ["mousemove", "keydown", "mousedown", "touchstart", "scroll", "click"];

    const handleActivity = () => resetTimer();

    events.forEach((e) => window.addEventListener(e, handleActivity, { passive: true }));
    resetTimer(); // start on mount

    return () => {
      events.forEach((e) => window.removeEventListener(e, handleActivity));
      clearTimeout(idleTimer.current);
      clearTimeout(warnTimer.current);
      clearInterval(countdownRef.current);
      dismissModal();
    };
  }, [
    resetTimer,
    dismissModal,
    enabled,
    idleExpiryMs,
    warnSeconds,
    showWarning,
    performLogout,
    continueSessionFromActivity,
  ]);
};

export default useIdleLogout;
